import {FiPlus} from "react-icons/fi";
import {Link} from "react-router";

import {ActionBarWrapper} from "@/components/actionBarWrapper/ActionBarWrapper.tsx";
import {BulkDeleteCrownAction} from "@/components/bulkDeleteCrownAction/BulkDeleteCrownAction.tsx";
import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FilterCategory, FilterCategoryType} from "@/components/filterDropdown/FilterDropdown.ts";
import {FilterDropdown} from "@/components/filterDropdown/FilterDropdown.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {useFilteredRowSelection} from "@/components/table/hooks/useFilteredRowSelection.ts";
import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {useAutoThrowSuspenseQuery} from "@/lib/hooks/useAutoThrowSuspenseQuery";
import {useTable} from "@/lib/hooks/useTable.ts";
import {GQL_SLA_REPORTS, SLAReport, SLAReportsResponse} from "@/lib/queries/reports.ts";
import {ChecksTags} from "@/pages/checks/list/components/CheckTags.tsx";
import {bulkDeleteSLAReports} from "@/pages/reports/sla/quickActions.ts";
import {SLAReportActions} from "@/pages/reports/sla/SLAReportActions.tsx";

import styles from "./SLAReportsList.module.scss";

interface SLAReportsListProps {
  onCreate?: () => void;
}

export const SLAReportsList = ({onCreate}: SLAReportsListProps) => {
  const {user} = useCurrentUserContext();
  const {search, searchDebounced, searchChanged, pagination, setPagination, filterSets, setFilterSets} = useTable();
  const {
    data: {slaReports, tags},
  } = useAutoThrowSuspenseQuery<SLAReportsResponse>(GQL_SLA_REPORTS, {
    variables: {
      ...pagination,
      search: searchDebounced,
      filters: filterSets,
    },
  });
  const {total, items} = slaReports;
  const {rowSelection, setRowSelection} = useFilteredRowSelection<SLAReport>({
    data: items,
    getRowId: (report) => report.id.toString(),
  });
  const isFiltered = search.trim().length > 0 || Object.keys(filterSets).length > 0;
  const filterFields: FilterCategory[] = [
    {
      key: "tags",
      type: FilterCategoryType.ARRAY,
      display: "Tags",
      options: tags.map((tag) => ({
        key: tag.id.toString(),
        display: tag.tag,
        color_hex: tag.color_hex,
      })),
      sorted: true,
      matchMode: {enabled: true},
    },
  ];
  const specs: ColumnSpec<SLAReport>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      meta: {headerClassName: styles.nameCell},
      accessor: (item) => (
        <Tooltip tooltipContent={item.name} delayMs={1000}>
          <div className="ellipsis-overflow">
            <Link to={`/reports/sla/${item.id}?range=${item.default_date_range}`}>{item.name}</Link>
          </div>
        </Tooltip>
      ),
    },
    {
      fieldName: "tags",
      fieldTitle: "Tags",
      meta: {headerClassName: styles.tagCell},
      accessor: (item) => <ChecksTags tags={item.tags} />,
    },
    {
      fieldName: "services_selected",
      fieldTitle: "Checks Selected",
      meta: {headerClassName: styles.checkCell},
      accessor: (item) => item.services_selected.length,
    },
    {
      fieldName: "quick_actions",
      meta: {headerClassName: styles.actionCell},
      accessor: (item) => <SLAReportActions id={item.id} name={item.name} range={item.default_date_range} />,
    },
  ];

  return (
    <>
      <ActionBarWrapper
        className="mb-3"
        left={
          <>
            <SearchInput variant="tableControlBar" value={search} onChange={searchChanged} />
            <FilterDropdown categories={filterFields} appliedFilterSets={filterSets} onApply={setFilterSets} />
          </>
        }
      />
      <Table
        fixedWidth
        minWidth={"600px"}
        specs={specs}
        initialState={{columnPinning: {right: ["quick_actions"]}}}
        rows={items}
        getRowId={(report) => report.id.toString()}
        rowSelection={rowSelection}
        setRowSelection={user?.has_write_access ? setRowSelection : undefined}
        actionBar={
          user?.has_write_access && (
            <BulkDeleteCrownAction
              selectedItems={rowSelection}
              setSelectedItems={setRowSelection}
              title="Delete SLA Reports"
              itemLabel="SLA Report"
              onDelete={(ids) => bulkDeleteSLAReports(ids.map(Number))}
            />
          )
        }
        noDataComponent={
          <TableEmptyStateCta
            colSpan={specs.length + (user?.has_write_access ? 1 : 0)}
            className="p-5"
            title={
              isFiltered ? "No SLA Reports found matching the filter(s) above." : "You do not have any SLA Reports."
            }
            cta={
              isFiltered || !onCreate ? undefined : (
                <Button size="large" variant="secondary" onClick={onCreate}>
                  <Icon icon={FiPlus} /> Create SLA Report
                </Button>
              )
            }
          />
        }
        pagination={pagination}
        updatePagination={setPagination}
        totalRows={total}
      />
    </>
  );
};
